// Invite wizard step 4 — review and submit. RON-5466 Phase 2. Prototype: `InviteStep4`,
// `ResourceReview`, `resLabel`.

import { useTranslation } from "react-i18next";

import { formatDateToEEEddMMM } from "../../../utils/CommonUtils.js";
import { VISIT_TYPE } from "../visitorConstants.js";
import VisitorDetailRow from "./VisitorDetailRow.jsx";
import VisitorEmailPreview from "./VisitorEmailPreview.jsx";
import VisitorResourceSummary from "./VisitorResourceSummary.jsx";

// Shared with the View screen so both render a visit identically.
const DetailRow = VisitorDetailRow;

const VisitorReviewStep = ({
  form,
  visitors,
  site,
  building,
  sendEmail,
  onToggleEmail,
  // Which of the two emails this toggle is about. See the label below.
  isEdit = false,
  canAssignDesk,
  canAssignParking,
}) => {
  const { t } = useTranslation("common");

  const isMultiDay = form.startDate !== form.endDate;

  const visitRows = [
    [t("react_visitors_field_start_date"), formatDateToEEEddMMM(form.startDate)],
    isMultiDay
      ? [t("react_visitors_field_end_date"), formatDateToEEEddMMM(form.endDate)]
      : null,
    [t("react_visitors_field_arrival"), form.arrivalTime],
    [t("react_visitors_field_site"), site?.name || "—"],
    building ? [t("react_visitors_field_building"), building.name] : null,
    [t("react_visitors_field_host"), form.host],
    [
      t("react_visitors_col_type"),
      form.type === VISIT_TYPE.VIP
        ? t("react_visitors_vip")
        : t("react_visitors_type_standard"),
    ],
  ].filter(Boolean);

  return (
    <>
      <div className="visitor-card">
        <p className="visitor-card__title">
          {t("react_visitors_review_visitors").replace("%s", String(visitors.length))}
        </p>
        <ul className="visitor-review__people">
          {visitors.map((visitor, index) => (
            <li className="visitor-review__person" key={index}>
              <span className="visitor-review__person-name">
                {visitor.name.trim() || t("react_visitors_unnamed")}
              </span>
              <span className="visitor-review__person-email">{visitor.email}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="visitor-card">
        <p className="visitor-card__title">{t("react_visitors_visit_title")}</p>
        {visitRows.map(([label, value]) => (
          <DetailRow key={label} label={label} value={value} />
        ))}
      </div>

      {/* Shared with the contractor wizard, so both reviews summarise resources identically. */}
      <VisitorResourceSummary
        form={form}
        canAssignDesk={canAssignDesk}
        canAssignParking={canAssignParking}
      />

      <div className="visitor-card">
        {/* The prototype's switch: label and hint on the left, the control on the right.
            A real checkbox still does the work — it is visually hidden rather than replaced, so
            the keyboard, the label association and screen readers all behave normally. The track
            and knob are drawn from its :checked state. */}
        <label className="visitor-toggle">
          <span className="visitor-toggle__text">
            <span className="visitor-toggle__label">
              {t(isEdit ? "react_visitors_send_update_email" : "react_visitors_send_email")}
            </span>
            <span className="visitor-toggle__hint">
              {/* The update hint carries a sentence the confirmation one does not: §7 sends the
                  email ONLY when something the visitor sees changed — name, email, date, arrival
                  time, site or building. A host who edits the purpose alone gets no email however
                  this is set, and without saying so that reads as a bug. */}
              {t(
                isEdit
                  ? "react_visitors_send_update_email_hint"
                  : "react_visitors_send_email_hint"
              )}
            </span>
          </span>
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={onToggleEmail}
            className="visitor-toggle__input"
          />
          <span className="visitor-toggle__track" aria-hidden="true">
            <span className="visitor-toggle__knob" />
          </span>
        </label>

        {sendEmail && (
          <div className="visitor-email-wrap">
            <p className="visitor-card__subtitle">
              {t("react_visitors_email_preview")}
            </p>
            <VisitorEmailPreview
              form={form}
              site={site}
              building={building}
              visitors={visitors}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default VisitorReviewStep;
